import { TableCell } from "@mui/material";
import { useEffect, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import { reactSelectSMStyle, styles } from "../../Utils/Common";
import { useDynamicGrid } from "./Context/useDynamicGrid";
import TextCalendarInputs from "./textCalendarInputs";
import {
  FilterTypeColumns,
  getDependentFieldKeysForControlColumn,
  isDropdownFilterType,
  isFilterDependentDataAction,
  isMultiSelectFilterType,
  isSelectableFilterType,
  isShowSelectedValueAction,
  isShowHideAction,
  optionMatchesStoredRowValue,
  parseJsonOptionsArray,
  storesLabelInRow,
} from "./filterDependentData";

/**
 * **`DynamicAddNewInputs`** is used when editing within a row.
 *
 * @param {Object} column - The column object representing the current column in the table.
 * @param {Object} item - The item or data associated with the current row being edited.
 * @param {number} columnHeaderIndex - The index of the column header that is being edited.
 * @param {(name: string, value: any) => void} onChange - Optional. When provided (e.g. in ApiDataModal), used instead of context setRows.
 * @param {any[]} externalDropdownOptions - Optional. When provided, used as dropdown options instead of parsing from inputFields.
 */

export function AddNewInputs({
  column,
  item,
  columnHeaderIndex,
  onChange: onChangeProp,
  externalDropdownOptions,
}: any) {
  const { setRows, inputFields, setInputFields, conditionalFields } =
    useDynamicGrid();
  const showHideConditionalFields = useMemo(
    () =>
      (conditionalFields || []).filter(
        (cfg: any) =>
          isShowHideAction(cfg) && Array.isArray(cfg?.Dependencies)
      ),
    [conditionalFields]
  );
  const showSelectedValueConditionalFields = useMemo(
    () =>
      (conditionalFields || []).filter(
        (cfg: any) =>
          isShowSelectedValueAction(cfg) &&
          Array.isArray(cfg?.DependentFilteredFields)
      ),
    [conditionalFields]
  );

  const fieldSourcesForDependentFilter = useMemo(
    () =>
      (inputFields || []).map((f: any) => ({
        name: f?.name,
        jsonOptionData: f?.jsonOptionData,
      })),
    [inputFields]
  );

  const visibilityMap = useMemo(() => {
    if (!showHideConditionalFields?.length) return {};

    const map: Record<string, boolean> = {};

    // Step 1: Collect all dependent fields across all configs
    const allDependentFields = new Set<string>();
    showHideConditionalFields.forEach((cfg: any) => {
      cfg.Dependencies.forEach((dep: any) => {
        dep.DependentFields.forEach((df: any) => {
          allDependentFields.add(df.Field);
        });
      });
    });

    // Step 2: Default all dependent fields to HIDDEN (true = hidden)
    allDependentFields.forEach((fieldName) => {
      map[fieldName] = true;
    });

    // Step 3: Apply visibility rules based on CURRENT control field values
    showHideConditionalFields.forEach((cfg: any) => {
      const controlField = cfg.Column;
      const selectedValue = item?.[controlField];

      if (!selectedValue) return; // No value = keep defaults (hidden)

      // Find matching dependency rule
      const matchingDep = cfg.Dependencies.find(
        (d: any) => d.value.toString() === selectedValue.toString()
      );

      if (!matchingDep) return; // No match = keep defaults (hidden)

      // Update visibility for fields in this dependency
      matchingDep.DependentFields.forEach((df: any) => {
        map[df.Field] = df.IsDisable; // true = hidden, false = visible
      });
    });

    return map;
  }, [
    showHideConditionalFields,
    // Watch all control field values
    ...showHideConditionalFields.map((f: any) => item?.[f.Column]),
  ]);

  // ✅ SINGLE EFFECT: Apply visibility map to inputFields
  // This runs ONLY when visibility actually changes
  useEffect(() => {
    if (!showHideConditionalFields?.length) return;

    setInputFields((prev: any) => {
      let hasChanges = false;
      const next = prev.map((field: any) => {
        const shouldHide = visibilityMap[field?.name];

        // Only update if this field is controlled AND visibility changed
        if (shouldHide !== undefined && field.show !== shouldHide) {
          hasChanges = true;
          return { ...field, show: shouldHide };
        }

        return field; // No change needed
      });

      return hasChanges ? next : prev;
    });
  }, [visibilityMap, showHideConditionalFields.length, setInputFields]);

  // ✅ SIMPLIFIED handleChange: Update value + clear hidden fields (or call parent when standalone)
  const handleChange = (name: string, value: any, Id: number) => {
    const applyShowSelectedValue = (
      rowLike: Record<string, any>,
      changedField: string,
      changedValue: any
    ) => {
      if (!showSelectedValueConditionalFields.length) return;

      showSelectedValueConditionalFields.forEach((cfg: any) => {
        if (cfg?.Column !== changedField) return;

        const controlField = fieldSourcesForDependentFilter.find(
          (f: any) => f?.name === changedField
        );
        const controlOptions = parseJsonOptionsArray(controlField?.jsonOptionData);
        const controlInputType = inputFields.find(
          (f: any) => f?.name === changedField
        )?.inputType;
        const selectedOption = controlOptions.find((opt: any) =>
          optionMatchesStoredRowValue(opt, changedValue, controlInputType)
        );

        cfg.DependentFilteredFields.forEach((dep: any) => {
          const targetField = dep?.Field;
          if (!targetField) return;

          const showedValueKey = dep?.ShowedValue;
          let mappedValue: any = "";

          if (selectedOption) {
            if (showedValueKey) {
              mappedValue = selectedOption?.[showedValueKey] ?? "";
            } else {
              mappedValue = selectedOption?.value ?? changedValue ?? "";
            }
          }

          if (
            (mappedValue === "" || mappedValue === null || mappedValue === undefined) &&
            !selectedOption
          ) {
            mappedValue = "";
          }

          rowLike[targetField] = mappedValue;
        });
      });
    };

    if (onChangeProp) {
      onChangeProp(name, value);
      const staged: Record<string, any> = { ...item, [name]: value };
      applyShowSelectedValue(staged, name, value);
      Object.keys(staged).forEach((key) => {
        if (key !== name && staged[key] !== item?.[key]) {
          onChangeProp(key, staged[key]);
        }
      });
      return;
    }
    setRows((curr: any) =>
      curr.map((x: any) => {
        if (x.Id !== Id) return x;

        // Start with base update
        const shouldApplyNumberBounds =
          isNumberTypeField &&
          name === column?.columnKey &&
          value !== "";
        let updatedValue = value;
        if (shouldApplyNumberBounds) {
          const numericValue = Number(value);
          if (Number.isFinite(numericValue)) {
            updatedValue = Math.max(0, numericValue);
            if (numberMaxFromConfig !== undefined) {
              updatedValue = Math.min(updatedValue, numberMaxFromConfig);
            }
          }
        }
        const updated = { ...x, [name]: updatedValue };
        applyShowSelectedValue(updated, name, value);

        const controllingField = inputFields.find((f: any) => f?.name === name);
        const isControllingDropdown = isDropdownFilterType(
          controllingField?.inputType
        );
        if (isControllingDropdown) {
          const dependentKeys = getDependentFieldKeysForControlColumn(
            conditionalFields,
            name
          );
          dependentKeys.forEach((fieldKey) => {
            const dependentField = inputFields.find(
              (f: any) => f?.name === fieldKey
            );
            const dependentInputType = dependentField?.inputType;
            if (isSelectableFilterType(dependentInputType)) {
              updated[fieldKey] = isMultiSelectFilterType(dependentInputType)
                ? []
                : "";
            }
          });
        }

        // If this is a control field, clear dependent fields that will be hidden
        if (showHideConditionalFields.length > 0) {
          const config = showHideConditionalFields.find(
            (f: any) => f.Column === name
          );

          if (config) {
            const matchingDep = config.Dependencies.find(
              (d: any) => d.value.toString() === value.toString()
            );

            // Get ALL dependent fields from this config
            const allDependentFields = config.Dependencies.flatMap((d: any) =>
              d.DependentFields.map((df: any) => df.Field)
            );

            // Clear fields that should be hidden
            allDependentFields.forEach((fieldName: string) => {
              const rule = matchingDep?.DependentFields.find(
                (df: any) => df.Field === fieldName
              );

              // Clear if: no rule exists OR rule says to hide (IsDisable = true)
              if (!rule || rule.IsDisable) {
                updated[fieldName] = "";
              }
            });
          }
        }

        return updated;
      })
    );
  };

  const isFieldDisabledByShowSelectedValue = useMemo(() => {
    const currentField = column?.columnKey;
    if (!currentField || !showSelectedValueConditionalFields.length) return false;

    return showSelectedValueConditionalFields.some((cfg: any) => {
      if (!Array.isArray(cfg?.DependentFilteredFields)) return false;
      return cfg.DependentFilteredFields.some(
        (dep: any) => dep?.Field === currentField && Boolean(dep?.IsDisabled)
      );
    });
  }, [showSelectedValueConditionalFields, column?.columnKey]);
  
  const rawFilterType = String(column?.filterColumnsType ?? "")
    .trim()
    .toLowerCase();
  const isNumberTypeField =
    rawFilterType === FilterTypeColumns.NUMBER ||
    rawFilterType.startsWith(`${FilterTypeColumns.NUMBER}:`);
  const numberMaxFromConfig = useMemo(() => {
    if (!rawFilterType.startsWith(`${FilterTypeColumns.NUMBER}:`)) {
      return undefined;
    }
    const maxPart = rawFilterType.split(":")[1];
    const parsedMax = Number(maxPart);
    return Number.isFinite(parsedMax) ? parsedMax : undefined;
  }, [rawFilterType]);

  const disabledFieldStyle = isFieldDisabledByShowSelectedValue
    ? {
      backgroundColor: "#eaeaea",
      cursor: "not-allowed" as const,
    }
    : {};

  // Handler specifically for DatePicker that converts event-style to direct params
  const handleDatePickerChange = (e: any) => {
    handleChange(e.target.name, e.target.value, item?.Id);
  };

  const filteredData = inputFields
    .map((item: any, index: number) =>
      item && item.jsonOptionData ? { ...item, defaultIndex: index } : null
    )
    .filter((item: any) => item !== null);

  const JSONOptions = filteredData.find(
    (item: any) => item.defaultIndex === columnHeaderIndex
  );

  let dropdownOptions: any[] = [];
  if (Array.isArray(externalDropdownOptions)) {
    dropdownOptions = externalDropdownOptions;
  } else if (JSONOptions?.jsonOptionData) {
    dropdownOptions = parseJsonOptionsArray(JSONOptions.jsonOptionData);
  }
  const columnFilterType = column?.filterColumnsType?.toLowerCase();
  if (
    isSelectableFilterType(columnFilterType) &&
    (conditionalFields || []).length > 0
  ) {
    const dependentConfigs = (conditionalFields || []).filter(
      (cfg: any) =>
        isFilterDependentDataAction(cfg) &&
        Array.isArray(cfg?.DependentFilteredFields) &&
        cfg.DependentFilteredFields.some(
          (dep: any) => dep?.Field === column?.columnKey
        )
    );

    let hasAppliedDependentFilter = false;

    dependentConfigs.forEach((cfg: any) => {
      const depRule = cfg.DependentFilteredFields.find(
        (dep: any) => dep?.Field === column?.columnKey
      );
      const filterOn = depRule?.FilterOn;
      const controllingColumn = cfg?.Column;
      if (!filterOn || !controllingColumn) return;

      const selectedControlValue = item?.[controllingColumn];
      let filterValue: any = undefined;

      // 1) Try control option metadata first: selected control option[FilterOn]
      const controlField = fieldSourcesForDependentFilter.find(
        (f: any) => f?.name === controllingColumn
      );
      const controlOptions = parseJsonOptionsArray(controlField?.jsonOptionData);
      const controlInputType = inputFields.find(
        (f: any) => f?.name === controllingColumn
      )?.inputType;
      const selectedControlOption = controlOptions.find((opt: any) =>
        optionMatchesStoredRowValue(opt, selectedControlValue, controlInputType)
      );
      if (selectedControlOption && selectedControlOption?.[filterOn] != null) {
        filterValue = selectedControlOption[filterOn];
      }

      // 2) Fallback to selected control value itself (requested behavior)
      if (
        (filterValue === undefined || filterValue === null || filterValue === "") &&
        selectedControlValue !== undefined &&
        selectedControlValue !== null &&
        selectedControlValue !== ""
      ) {
        filterValue = selectedControlValue;
      }

      // 3) Fallback to row[FilterOn] if available
      if (
        (filterValue === undefined || filterValue === null || filterValue === "") &&
        item?.[filterOn] !== undefined &&
        item?.[filterOn] !== null &&
        item?.[filterOn] !== ""
      ) {
        filterValue = item?.[filterOn];
      }

      // If this dependency can't resolve a usable filter value, skip it
      // and continue with other matching dependency configs.
      if (
        filterValue === undefined ||
        filterValue === null ||
        filterValue === ""
      ) {
        return;
      }

      hasAppliedDependentFilter = true;
      dropdownOptions = dropdownOptions.filter((opt: any) => {
        return String(opt?.[filterOn] ?? "") === String(filterValue ?? "");
      });
    });

    // Dependent fields should not show any options until at least one
    // controlling dependency can provide a valid filter value.
    if (dependentConfigs.length > 0 && !hasAppliedDependentFilter) {
      dropdownOptions = [];
    }
  }

  const containsComma = column?.filterColumnsType?.toLowerCase().includes(",");
  const commanSeparatedSecondValue = column?.filterColumnsType?.split(",")[1];

  // Check if this field is required
  const isRequired =
    column?.isRequired === true && column?.isShowOnUi === true;

  return (
    <TableCell>
      <div
        className={`d-flex ${isRequired ? " required" : ""} w-100`}
      >
        {column?.filterColumnsType?.toLowerCase() === FilterTypeColumns.TEXT && (
          <input
            type="text"
            className={`form-control ${isFieldDisabledByShowSelectedValue ? "" : "bg-white"} mb-lg-0 h-30px rounded-2 w-100`}
            placeholder={column?.columnLabel}
            value={item?.[column?.columnKey]}
            name={column?.columnKey}
            disabled={isFieldDisabledByShowSelectedValue}
            style={disabledFieldStyle}
            onChange={(e: any) =>
              handleChange(e.target.name, e.target.value, item?.Id)
            }
          />
        )}
        {isNumberTypeField && (
          <input
            type="number"
            className={`form-control ${isFieldDisabledByShowSelectedValue ? "" : "bg-white"} mb-lg-0 h-30px rounded-2 w-100`}
            placeholder={column?.columnLabel}
            value={item?.[column?.columnKey] ?? ""}
            name={column?.columnKey}
            min={0}
            max={numberMaxFromConfig}
            disabled={isFieldDisabledByShowSelectedValue}
            style={disabledFieldStyle}
            onChange={(e: any) => {
              const inputValue = e.target.value;
              if (inputValue === "") {
                handleChange(e.target.name, "", item?.Id);
                return;
              }
              const numericValue = Number(inputValue);
              if (!Number.isFinite(numericValue)) return;
              let nextValue = Math.max(0, numericValue);
              if (numberMaxFromConfig !== undefined) {
                nextValue = Math.min(nextValue, numberMaxFromConfig);
              }
              handleChange(e.target.name, nextValue, item?.Id);
            }}
          />
        )}
        {isDropdownFilterType(columnFilterType) && (
            <div className="flex-grow-1 w-100" style={{ minWidth: 0 }}>
              <Select
                menuPortalTarget={document.body}
                theme={(theme) => styles(theme)}
                name={column?.columnKey}
                value={
                  dropdownOptions?.length &&
                  dropdownOptions?.filter((option: any) => {
                    return storesLabelInRow(columnFilterType)
                      ? option.label === item?.[column?.columnKey]
                      : option.value === item?.[column?.columnKey];
                  })
                }
                options={dropdownOptions}
                styles={{
                  ...reactSelectSMStyle,
                  control: (base: any, state: any) => ({
                    ...reactSelectSMStyle.control(base, state),
                    width: "100%",
                    backgroundColor: state.isDisabled ? "#e9ecef" : base.backgroundColor,
                    cursor: state.isDisabled ? "not-allowed" : base.cursor,
                    opacity: 1,
                  }),
                  singleValue: (base: any, state: any) => ({
                    ...base,
                    color: state.isDisabled ? "#6c757d" : base.color,
                  }),
                  placeholder: (base: any, state: any) => ({
                    ...base,
                    color: state.isDisabled ? "#6c757d" : base.color,
                  }),
                }}
                isDisabled={isFieldDisabledByShowSelectedValue}
                placeholder={column?.columnLabel}
                onChange={(e: any) =>
                  handleChange(
                    column?.columnKey,
                    storesLabelInRow(columnFilterType) ? e.label : e.value,
                    item?.Id
                  )
                }
              />
            </div>
          )}
        {isMultiSelectFilterType(columnFilterType) && (
          <div className="flex-grow-1 w-100" style={{ minWidth: 0 }}>
            <Select
              menuPortalTarget={document.body}
              theme={(theme) => styles(theme)}
              name={column?.columnKey}
              value={
                Array.isArray(item?.[column?.columnKey])
                  ? item?.[column?.columnKey]
                  : []
              }
              options={dropdownOptions}
              isMulti={true}
              isSearchable={true}
              styles={{
                ...reactSelectSMStyle,
                control: (base: any, state: any) => ({
                  ...reactSelectSMStyle.control(base, state),
                  width: "100%",
                  backgroundColor: state.isDisabled ? "#e9ecef" : base.backgroundColor,
                  cursor: state.isDisabled ? "not-allowed" : base.cursor,
                  opacity: 1,
                }),
                placeholder: (base: any, state: any) => ({
                  ...base,
                  color: state.isDisabled ? "#6c757d" : base.color,
                }),
              }}
              isDisabled={isFieldDisabledByShowSelectedValue}
              placeholder={column?.columnLabel}
              onChange={(selectedOptions: any) =>
                handleChange(
                  column?.columnKey,
                  Array.isArray(selectedOptions) ? selectedOptions : [],
                  item?.Id
                )
              }
            />
          </div>
        )}
        {column?.filterColumnsType?.toLowerCase() ===
          FilterTypeColumns.DATEPICKER && (
            <SearchDatePicker
              value={item?.[column?.columnKey]}
              name={column?.columnKey}
              disabled={isFieldDisabledByShowSelectedValue}
              onChange={handleDatePickerChange}
            />
          )}
        {containsComma &&
          commanSeparatedSecondValue.toLowerCase() ===
          FilterTypeColumns.TEXT_DATE && (
            <TextCalendarInputs
              column={column}
              item={item}
              handleInputsChange={handleChange}
            />
          )}
      </div>
    </TableCell>
  );
}
// const convertToDateInputFormat = (dateString: string) => {
//   if (!dateString) return "";

//   // Handles formats like "2024/04/08" or "2024-04-08"
//   const normalized = dateString.replaceAll("/", "-");
//   const date = new Date(normalized);

//   if (isNaN(date.getTime())) return ""; // Invalid date check

//   const year = date.getFullYear();
//   const month = `0${date.getMonth() + 1}`.slice(-2); // month is 0-indexed
//   const day = `0${date.getDate()}`.slice(-2);

//   return `${year}-${month}-${day}`;
// };

